import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Chip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { formatDateTime } from '../../utils/formatters';
import { getTickets } from '../../api/endpoints/support';

const priorityColors = { urgent: 'error', high: 'warning', medium: 'info', low: 'default' };

const TicketList = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.q = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const res = await getTickets(params);
      setTickets(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter, priorityFilter, categoryFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const columns = [
    { id: '_id', label: 'ID', render: (row) => <Typography variant="body2" fontFamily="monospace" fontWeight={500}>{(row._id || '').slice(-8)}</Typography> },
    { id: 'subject', label: 'Subject', render: (row) => (
      <Typography variant="body2" fontWeight={500} sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.subject}</Typography>
    )},
    { id: 'user', label: 'User', render: (row) => row.userId?.name || row.user?.name || '-' },
    { id: 'category', label: 'Category', render: (row) => <Chip label={row.category} size="small" variant="outlined" /> },
    { id: 'priority', label: 'Priority', render: (row) => <Chip label={row.priority} size="small" color={priorityColors[row.priority] || 'default'} /> },
    { id: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
    { id: 'assignedTo', label: 'Assigned To', render: (row) => row.assignedTo?.name || '-' },
    { id: 'createdAt', label: 'Date', render: (row) => formatDateTime(row.createdAt) },
  ];

  return (
    <Box>
      <PageHeader title="Support Tickets" subtitle="Manage support tickets from users" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by subject or user..."
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)}>
            <MenuItem value="all">All Priority</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
            <MenuItem value="all">All Category</MenuItem>
            <MenuItem value="technical">Technical</MenuItem>
            <MenuItem value="billing">Billing</MenuItem>
            <MenuItem value="kyc">KYC</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <DataTable
        columns={columns}
        rows={tickets}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        loading={loading}
        getRowId={(row) => row._id}
      />
    </Box>
  );
};

export default TicketList;
